/* eslint-disable react/prop-types */
import React from 'react';
import { Box, Flex, Grid, GridItem, Spinner, Text } from '@chakra-ui/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DailyProfitabilityBarColors } from 'utils/StaticData';
import PropTypes from 'prop-types';
import { formatCurrency } from 'utils/CommonFunction';

const GrossRevenueProfitabilityGraph = ({
  isLoading,
  graphData,
  regionNameMap,
  currency,
}) => {
  return (
    <>
      <Grid
        bg="white"
        p={{ base: '4', md: '5' }}
        templateColumns="repeat(12, 1fr)"
        gap={{ base: '16px', lg: '24px' }}
        boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
        borderRadius="20px"
        border="1px solid #D9D9D9"
        w="full"
        mb="20px"
      >
        {/* Gross Revenue Graph */}
        <GridItem colSpan={{ base: '12', lg: '12' }}>
          <Box w="full" h="full" bg="white">
            <Flex justify="space-between" align="baseline" mb={4}>
              <Text
                fontSize={{ base: '16px', md: '24px' }}
                fontWeight="semibold"
                color="primary"
              >
                Gross Revenue
              </Text>
              {!isLoading && (
                <Flex flexWrap="wrap" align="left" flexDirection="column">
                  {[
                    ...new Map(
                      graphData?.map((item) => [item.regionId, item]),
                    ).values(),
                  ].map((item, index) => (
                    <Flex
                      key={index}
                      align={{ base: 'flex-start', lg: 'center' }}
                      ml="4"
                    >
                      <Box
                        w={{ base: '8px', lg: '10px' }}
                        h={{ base: '8px', lg: '10px' }}
                        bg={DailyProfitabilityBarColors[item.regionId]}
                        borderRadius="50%"
                        mt={{ base: '4px', lg: '0' }}
                        mr="2"
                        flexShrink="0"
                      />
                      <Text fontSize={{ base: '10px', md: '12px' }}>
                        {regionNameMap[item?.regionId] ||
                          `Region ${item?.regionId}`}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>

            {isLoading ? (
              <>
                <Flex justify="center" alignItems="center" height="300px">
                  <Spinner size="lg" color="black.500" />
                </Flex>
              </>
            ) : graphData?.length > 0 ? (
              <>
                <Box overflowX="auto" className="custom-scrollbar">
                  <Box minWidth={`${Math.max(graphData?.length * 50, 600)}px`}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={graphData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: graphData?.length > 10 ? 70 : 60,
                        }}
                        barCategoryGap={graphData?.length < 5 ? '10%' : '20%'}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={true}
                          vertical={false}
                        />

                        {/* X-axis: Merchant names */}
                        <XAxis
                          dataKey="merchantName"
                          interval={0}
                          tick={(props) => {
                            const { x, y, payload } = props;
                            const name = payload.value;
                            const maxLength = 10;
                            const truncatedName =
                              name.length > maxLength
                                ? `${name.substring(0, maxLength)}…`
                                : name;

                            return (
                              <text
                                x={x}
                                y={y + 10}
                                textAnchor={
                                  graphData?.length < 4 ? 'middle' : 'end'
                                }
                                fontSize={12}
                                transform={
                                  graphData?.length < 4
                                    ? undefined
                                    : `rotate(-60, ${x}, ${y})`
                                }
                              >
                                {truncatedName}
                              </text>
                            );
                          }}
                        />

                        {/* Y-axis: Revenue values */}
                        <YAxis
                          interval={0}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) =>
                            formatCurrency(value, currency)
                          }
                        />

                        {/* Tooltip */}
                        <Tooltip
                          formatter={(value) => formatCurrency(value, currency)}
                          labelFormatter={(name) => `Merchant: ${name}`}
                        />

                        {/* Bar with label on top */}
                        <Bar
                          dataKey="total"
                          name="Revenue"
                          radius={[5, 5, 0, 0]}
                        >
                          {graphData?.map((entry, index) => (
                            <>
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  DailyProfitabilityBarColors[
                                    entry?.regionId
                                  ] || ''
                                }
                              />
                            </>
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </>
            ) : (
              <Flex justify="center" alignItems="center" height="300px">
                <Text fontSize="18px" fontWeight="bold" color="gray.500">
                  No records available
                </Text>
              </Flex>
            )}
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};

GrossRevenueProfitabilityGraph.propTypes = {
  isLoading: PropTypes.bool,
  graphData: PropTypes.arrayOf(
    PropTypes.shape({
      total: PropTypes.number,
      regionId: PropTypes.number,
      merchantName: PropTypes.string,
    }),
  ),
  regionNameMap: PropTypes.objectOf(PropTypes.string),
  currency: PropTypes.string,
};

export default GrossRevenueProfitabilityGraph;
